import { motion } from "framer-motion";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/layout/Footer";
import Header from "../../components/layout/Header";
import { FilePenLine, Mail } from "lucide-react";
import Meta from "../../components/Meta/Meta";

export default function HowToUse() {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className=" min-h-screen "
    >
      <Meta
        title="How to Use MaxiPitch | AI Sales Automation Guide"
        description="Learn how to use MaxiPitch to automate lead generation, manage outbound campaigns, and improve sales performance with easy AI-powered automation tools."
        url="https://maxipitch.com/how-to-use"
      />
      <Header />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="py-8 bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5 text-center pt-12 max-md:pt-8"
      >
        <h1 className=" bg-gradient-to-r from-gray-900 via-indigo-800 to-purple-800 bg-clip-text text-transparent ">
          How To Use Maxipitch
        </h1>
        <p className="text-xl max-md:text-base text-gray-500 max-w-2xl mx-auto max-md:px-2">
          An AI-powered lead generation and outreach tool designed to help you
          find prospects and start conversations fasters
        </p>
      </motion.div>
      <div className="max-w-7xl mx-auto ">
        <div className="text-center mb-6 "></div>

        {/* Steps Container */}
        <div className="relative">
          {/* Vertical Timeline Line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-indigo-200 hidden md:block"></div>

          {/* Steps */}
          <div className="space-y-8">
            <Step
              number="01"
              title="Generate Leads"
              content={
                <div className="space-y-3">
                  <p className="text-gray-700">
                    Enter a descriptive prompt about your target audience
                  </p>
                  <div className="bg-gray-50 py-1 px-2 rounded-lg border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Example prompt</p>
                    <p className="text-gray-900 font-medium">
                      "General managers of hotels and Resorts"
                    </p>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3"></span>
                      Maxipitch will generate leads with names, location,
                      company, and job titles,linkedIn url
                    </li>
                  </ul>
                </div>
              }
            />

            <Step
              number="02"
              title="Send Emails & Sign Up"
              content={
                <div className="space-y-3">
                  <p className="text-gray-700">
                    Click <b className="px-1">"Send Email"</b> to begin. New
                    users will see a registration popup
                  </p>
                  <div className="bg-gray-50 p-1.5 rounded-lg border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Note</p>
                    <p className="text-gray-900">
                      Complete sign-up, then click{" "}
                      <b className="px-1">"Send Email"</b> again to proceed
                    </p>
                  </div>
                </div>
              }
            />

            <Step
              number="03"
              title="Connect Email Account"
              content={
                <div className="space-y-4">
                  <p className="text-gray-700">
                    Choose your preferred connection method:
                  </p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full flex items-center justify-center mr-3">
                          <span className="text-sm font-medium">A</span>
                        </div>
                        <h4 className="font-semibold text-gray-900">
                          OAuth Connection
                        </h4>
                      </div>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-center">
                          <span className="w-1 h-1 bg-gray-400 rounded-full mr-2"></span>
                          Gmail
                        </li>
                        <li className="flex items-center">
                          <span className="w-1 h-1 bg-gray-400 rounded-full mr-2"></span>
                          Zoho Mail
                        </li>
                        <li className="flex items-center">
                          <span className="w-1 h-1 bg-gray-400 rounded-full mr-2"></span>
                          Outlook
                        </li>
                      </ul>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full flex items-center justify-center mr-3">
                          <span className="text-sm font-medium">B</span>
                        </div>
                        <h4 className="font-semibold text-gray-900">
                          SMTP Connection
                        </h4>
                      </div>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-center">
                          <span className="w-1 h-1 bg-gray-400 rounded-full mr-2"></span>
                          Email address
                        </li>
                        <li className="flex items-center">
                          <span className="w-1 h-1 bg-gray-400 rounded-full mr-2"></span>
                          password
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              }
            />

            <Step
              number="04"
              title="Create Campaign & Email Content"
              content={
                <div className="space-y-4">
                  <p className="text-gray-700">
                    Set up your campaign and choose how you want to create your
                    email content
                  </p>

                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                      Enter a campaign name
                    </li>

                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                      Choose how to create your email:
                    </li>

                    <li className="ml-6 text-gray-800">
                      •{" "}
                      <span className="font-medium">Create Email Template</span>{" "}
                      – Add a prompt and generate a new AI-powered email
                    </li>

                    <li className="ml-6 text-gray-800">
                      • <span className="font-medium">Use Template</span> –
                      Select from your previously created and saved email
                      templates
                    </li>
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                      Edit or customize the email content if required
                    </li>
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                      Preview the selected template before proceeding
                    </li>
                    {/* ✅ NEW POINT ADDED HERE */}
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                      Click " Send a preview email" to check how this email
                      looks in your inbox
                    </li>
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                      Click “Save & Send Emails” to apply the updated version to
                      the current campaign
                    </li>
                  </ul>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
                    💡 Using templates helps you launch campaigns faster by
                    reusing proven email content
                  </div>
                </div>
              }
            />

            <Step
              number="05"
              title="Review Campaign Details"
              content={
                <div className="space-y-4">
                  <p className="text-gray-700">
                    Before sending, review your campaign details to ensure
                    everything looks correct
                  </p>
                  <ul className="space-y-3 text-base text-gray-600">
                    <li className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                      <span className="font-medium text-gray-900 sm:min-w-32">
                        Campaign Name:
                      </span>
                      <span className="text-gray-500">
                        Hotel Outreach – January 2025
                      </span>
                    </li>

                    <li className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                      <span className="font-medium text-gray-900 sm:min-w-32">
                        Email Provider:
                      </span>
                      <span className="text-gray-500">
                        Zoho / Gmail /Outlook/ SMTP
                      </span>
                    </li>

                    <li className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                      <span className="font-medium text-gray-900 sm:min-w-32">
                        Recipient Count:
                      </span>
                      <span className="text-gray-500">
                        3,500 decision-makers
                      </span>
                    </li>

                    <li className="flex flex-col sm:flex-row gap-1 sm:gap-3">
                      <span className="font-medium text-gray-900 sm:min-w-32">
                        Email Preview:
                      </span>
                      <span className="text-gray-500">
                        “Hi, we noticed your role at XYZ.com and wanted to
                        connect regarding…”
                      </span>
                    </li>
                  </ul>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
                    💡 Make sure personalization tokens, subject line, and tone
                    match your outreach goal before proceeding
                  </div>
                </div>
              }
            />

            <Step
              number="06"
              title="Send Emails & Confirm"
              content={
                <div className="space-y-3">
                  <p className="text-gray-700">
                    Finalize and launch your campaign
                  </p>
                  <div className="bg-blue-50 border border-blue-100  text-blue-700 p-3 max-md:p-1.5 max-md:text-sm rounded-lg">
                    <p className="font-medium">
                      🎉 First 50 emails will be sent immediately upon
                      confirmation
                    </p>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                      Click <b className="px-1">"Send Email"</b>
                    </li>
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                      Confirm in the popup
                    </li>
                  </ul>
                </div>
              }
            />

            {/* <Step
              number="07"
              title="Subscription & Daily Email Flow"
              content={
                <div className="space-y-3">
                  <p className="text-gray-700">
                    After subscribing, your automated daily outreach begins
                    instantly
                  </p>

                  <div className="border border-gray-200 p-3 rounded-lg space-y-2">
                    <p className="text-gray-900 font-medium flex items-center gap-4">
                      <Mail size={18} /> Day 1: Send 25 fresh outreach emails
                    </p>
                    <p className="text-gray-900 font-medium flex items-center gap-4">
                      <FilePenLine size={18} /> From Day 2 onwards:
                    </p>
                    <ul className="list-disc pl-5 text-gray-700">
                      <li>25 new fresh emails sent daily</li>
                      <li>25 follow-up emails sent automatically</li>
                    </ul>
                  </div>

                  <p className="text-sm text-gray-600">
                    This ensures a consistent flow of new leads while
                    maintaining follow-ups for better engagement.
                  </p>
                </div>
              }
            /> */}

            <Step
              number="07"
              title="Fresh Email Sending"
              content={
                <div className="space-y-3">
                  <p className="text-gray-700">
                    After you subscribe, your email sending starts
                    automatically:
                  </p>

                  <div className="border border-gray-200 p-3 rounded-lg text-gray-700 space-y-2">
                    <p>
                      📩{" "}
                      <span className="font-medium">
                        Day 1 (next day after payment):
                      </span>{" "}
                      25 fresh new emails are sent automatically
                    </p>
                    <p>
                      📩 <span className="font-medium">Day 2:</span> 25 fresh
                      new emails are sent
                    </p>
                  </div>

                  <p className="text-sm text-gray-600">
                    You start reaching new people every day with fresh emails.
                  </p>
                </div>
              }
            />

            <Step
              number="08"
              title="Follow-up Emails"
              content={
                <div className="space-y-3">
                  <p className="text-gray-700">
                    Stay connected with your leads through automated follow-ups.
                  </p>

                  <div className="border border-gray-200 p-3 rounded-lg text-gray-700">
                    <p className="font-medium">🔄 From Day 3 onwards:</p>
                    <ul className="list-disc pl-5 mt-1">
                      <li>
                        25 fresh new emails + 25 follow-up emails sent
                        automatically
                      </li>
                    </ul>
                  </div>

                  <p className="text-sm text-gray-600">
                    This means you reach new leads and follow up with existing
                    ones every day for better engagement.
                  </p>
                </div>
              }
            />
            <Step
              number="09"
              title="Campaign Tracking "
              content={
                <div className="space-y-4">
                  <p className="text-gray-700">
                    All your created campaigns are available in the{" "}
                    <b>Campaigns</b> tab, where you can monitor progress and
                    manage outreach activity
                  </p>

                  <ul className="space-y-3 text-gray-600 text-base">
                    <li className="flex items-start gap-3">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
                      <p>
                        View all campaigns with their current status such as{" "}
                        <b className="px-1">Active</b>,{" "}
                        <b className="px-1">Completed</b>, or{" "}
                        <b className="px-1">Failed</b>
                      </p>
                    </li>

                    <li className="flex items-start gap-3">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
                      <p>Search and filter campaigns for quick access</p>
                    </li>

                    <li className="flex items-start gap-3">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
                      <p>Click on any campaign to open its detailed view</p>
                    </li>
                  </ul>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
                    💡 The campaign list gives you a high-level overview before
                    diving into detailed performance insights
                  </div>
                </div>
              }
            />

            <Step
              number="10"
              title="Campaign Details & Performance"
              content={
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <p className="text-sm text-blue-700 font-medium">
                      Once inside a campaign, you can track delivery status,
                      review email content, and analyze performance metrics in
                      real time
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-2 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-500">Campaign Status</p>
                      <p className="font-semibold text-gray-900">
                        Active / Completed
                      </p>
                    </div>

                    <div className="text-center p-2 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-500">Total Leads</p>
                      <p className="font-semibold text-gray-900">
                        Recipients count
                      </p>
                    </div>

                    <div className="text-center p-2 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-500">Emails Sent</p>
                      <p className="font-semibold text-gray-900">Live count</p>
                    </div>

                    <div className="text-center p-2 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-500">Email in queued</p>
                      <p className="font-semibold text-gray-900">
                        Auto-updated
                      </p>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Email Template Preview
                    </p>
                    <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-600 max-h-32 overflow-y-auto">
                      Preview the exact email content sent to your leads before
                      and after campaign launch
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Campaign Performance Graphs
                    </p>
                    <p className="text-sm text-gray-600">
                      Visual graphs display daily performance, delivery success
                      rate, and engagement trends to help you optimize future
                      campaigns
                    </p>
                  </div>
                </div>
              }
            />
            <Step
              number="11"
              title="Email Templates Management"
              content={
                <div className="space-y-3">
                  <p className="text-gray-700">
                    Manage all your AI-generated and saved email templates from
                    one place
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li>• View all generated and saved email templates</li>
                    <li>• Edit templates anytime before sending</li>
                    <li>• Generate new AI-powered emails instantly</li>
                    <li>• Reuse templates across multiple campaigns</li>
                  </ul>
                </div>
              }
            />
            <Step
              number="12"
              title="Profile Management"
              content={
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center p-1 hover:bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-900 min-w-40">
                      Personal Details
                    </span>
                    <span className="text-gray-500">
                      Update name, email, company
                    </span>
                  </li>
                  <li className="flex items-center p-1 hover:bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-900 min-w-40">
                      Email Accounts
                    </span>
                    <span className="text-gray-500">Manage connections</span>
                  </li>
                  <li className="flex items-center p-1 hover:bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-900 min-w-40">
                      Account Settings
                    </span>
                    <span className="text-gray-500">
                      Security & preferences
                    </span>
                  </li>
                </ul>
              }
            />

            <Step
              number="13"
              title="Billing & Subscription"
              content={
                <div className="space-y-3">
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                      View current plan & billing history
                    </li>
                  </ul>
                </div>
              }
            />

            <Step
              number="14"
              title="Contact & Support"
              content={
                <div className="space-y-3  mb-4">
                  <p className="text-gray-700">
                    We're here to help you succeed
                  </p>
                  <div className="bg-gray-50 p-2 rounded-lg border border-gray-200">
                    <p className="text-gray-900">
                      Access FAQs, contact support for billing or campaign help,
                      and get assistance with email setup
                    </p>
                  </div>
                </div>
              }
            />
          </div>
        </div>

        {/* Footer */}
        <div className=" text-center mt-4 pt-4 max-md:px-4">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full mb-4">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <p className="text-lg font-medium text-gray-900 mb-2 max-md:text-base">
            Transform leads into revenue with AI-powered precision
          </p>
          <p className="text-gray-600 max-md:text-sm">
            <button
              className="text-blue-700 underline font-medium"
              onClick={() => navigate("/contact-us")}
            >
              Need assistance?
            </button>{" "}
            Our support team is always ready to help
          </p>
        </div>
      </div>
      <div className="py-8">
        <Footer />
      </div>
    </motion.div>
  );
}

/* Reusable Step Component */
function Step({ number, title, content }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="flex group "
    >
      {/* Timeline Number */}
      <div className="hidden md:flex flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full items-center justify-center mr-4 z-10 max-md:px-4 ">
        <span className="font-bold">{number}</span>
      </div>

      {/* Content Card */}
      <motion.div
        whileHover={{ scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300 }}
        className="flex-1 border border-gray-200 rounded-xl p-3 hover:border-gray-300 hover:shadow-sm transition-all duration-200 bg-white mb-4  max-md:mx-4"
      >
        <div className="flex items-center mb-4">
          <div className="md:hidden w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full flex items-center justify-center mr-3">
            <span className="font-bold text-sm">{number}</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        </div>
        <div className="text-gray-700">{content}</div>
      </motion.div>
    </motion.div>
  );
}
